// Data category enumeration
export enum DataCategory {
  Overview = 'overview',
  Perp = 'perp',
  Rwa = 'rwa',
  Spot = 'spot',
}

// ==================== Overview Types ====================

export interface OverviewMetrics {
  cumulativeTradingVolume: string;
  cumulativeTradingVolumeChange: number;
  totalUsers: number;
  totalUsersChange: number;
  totalTransactions: number;
  dailyActiveUsers: number;
  tvl: number;
  tvlChange: number;
  activeUsers7D: number;
  activeUsers30D: number;
  arpu: string;
  ltv: string;
  cac: string;
  crr30D: number;
  totalFeesUsd: number;
  dailyActiveUsersChange: number;
  totalTransactionsChange: number;
  totalFeesUsdChange: number;
  totalGasNative: number;
  totalGasNativeChange: number;
}

export interface OverviewResponse {
  createdAt: string;
  date: string;
  overview: OverviewMetrics;
}

// ==================== Perp Types ====================

export interface TopMarket {
  market: string;
  address: string;
  volumeUsd: string;
  oiNotionalUsd: string;
}

export interface VolumeDataPoint {
  date: string;
  volumeUsd: string;
  cumulativeVolumeUsd: string;
  topMarkets: TopMarket[];
}

export interface VolumeResponse {
  createdAt: string;
  date: string;
  data: VolumeDataPoint[];
}

export interface OpenInterestDataPoint {
  date: string;
  oiNotionalUsd: string;
  topMarkets: TopMarket[];
}

export interface OpenInterestResponse {
  createdAt: string;
  date: string;
  data: OpenInterestDataPoint[];
}

export interface UsersDataPoint {
  date: string;
  users: number;
  newUsers: number;
  cumulativeUsers: number;
}

export interface UsersResponse {
  createdAt: string;
  date: string;
  data: UsersDataPoint[];
}

export interface TVLDataPoint {
  date: string;
  tvlUsd: string;
}

export interface TVLResponse {
  createdAt: string;
  date: string;
  data: TVLDataPoint[];
}

export interface TransactionsDataPoint {
  date: string;
  transactions: number;
  cumulativeTransactions: number;
}

export interface TransactionsResponse {
  createdAt: string;
  date: string;
  data: TransactionsDataPoint[];
}

export interface RevenueDataPoint {
  date: string;
  feesUsd: string;
  cumulativeFeesUsd: string;
}

export interface RevenueResponse {
  createdAt: string;
  date: string;
  data: RevenueDataPoint[];
}

export interface GasConsumptionDataPoint {
  date: string;
  gasNative: string;
  cumulativeGasNative: string;
}

export interface GasConsumptionResponse {
  createdAt: string;
  date: string;
  data: GasConsumptionDataPoint[];
}

// ==================== Spot Types ====================
// Note: All spot endpoints currently return null data

export interface SpotEmptyResponse {
  createdAt: string;
  date: string;
  data: null;
}

// ==================== Unified Analytics Data Types ====================

export interface AnalyticsData<T = unknown> {
  category: DataCategory;
  metric?: string;
  data: T;
  timestamp: number;
  createdAt?: string;
}

export interface AllAnalyticsData {
  overview: AnalyticsData<OverviewMetrics> | null;
  perp: {
    volume: AnalyticsData<VolumeDataPoint[]> | null;
    openInterest: AnalyticsData<OpenInterestDataPoint[]> | null;
    users: AnalyticsData<UsersDataPoint[]> | null;
    tvl: AnalyticsData<TVLDataPoint[]> | null;
    transactions: AnalyticsData<TransactionsDataPoint[]> | null;
    revenue: AnalyticsData<RevenueDataPoint[]> | null;
    gasConsumption: AnalyticsData<GasConsumptionDataPoint[]> | null;
  };
  rwa: {
    volume: AnalyticsData<VolumeDataPoint[]> | null;
    users: AnalyticsData<UsersDataPoint[]> | null;
    tvl: AnalyticsData<TVLDataPoint[]> | null;
    transactions: AnalyticsData<TransactionsDataPoint[]> | null;
    revenue: AnalyticsData<RevenueDataPoint[]> | null;
    gasConsumption: AnalyticsData<GasConsumptionDataPoint[]> | null;
  };
  spot: {
    volume: AnalyticsData<null> | null;
    users: AnalyticsData<null> | null;
    tvl: AnalyticsData<null> | null;
    transactions: AnalyticsData<null> | null;
    revenue: AnalyticsData<null> | null;
    gasConsumption: AnalyticsData<null> | null;
  };
}

// ==================== Fetch Options ====================

export interface FetchOptions {
  signal?: AbortSignal;
}
